/**
 * SchemaSection — generic renderer for one device-configuration schema section.
 *
 * Renders the section's typed fields (int/float/str/bool/choice/time) bound to
 * Formik values nested under the section key (e.g. `network.server_port`). The
 * backend ships the schema; this component pivots on each field's `type` so the
 * unit-type `*Form` variants don't repeat field-rendering logic.
 *
 * Contains:
 * - SchemaSection — section heading + field stack (default export)
 */
import { Box, FormControlLabel, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

const NUMERIC_TYPES = new Set(['int', 'float']);

function SchemaField({ sectionKey, field }) {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const name = `${sectionKey}.${field.name}`;
  const label = t(field.label_key, field.name);
  const helperText = field.help_key ? t(field.help_key, '') : undefined;
  const value = values?.[sectionKey]?.[field.name] ?? '';

  if (field.type === 'bool') {
    return (
      <FormControlLabel
        control={<Switch checked={Boolean(value)} onChange={e => setFieldValue(name, e.target.checked)} />}
        label={label}
      />
    );
  }

  if (field.type === 'choice') {
    // DB-sourced options (e.g. UnitThresholdActions) carry a literal `label`/
    // `description` alongside label_key, since their names have no static
    // i18n entry; label_key wins when it resolves, the literal is the fallback.
    const selectedOption = (field.options || []).find(opt => String(opt.value) === String(value));
    const choiceHelperText = helperText ?? selectedOption?.description ?? undefined;
    return (
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        helperText={choiceHelperText}
        value={value ?? ''}
        onChange={e => setFieldValue(name, e.target.value)}
      >
        {(field.options || []).map(opt => (
          <MenuItem key={String(opt.value)} value={opt.value}>
            {t(opt.label_key, opt.label ?? String(opt.value))}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  const isNumeric = NUMERIC_TYPES.has(field.type);
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      helperText={helperText}
      required={field.required}
      type={field.type === 'time' ? 'time' : isNumeric ? 'number' : 'text'}
      InputLabelProps={field.type === 'time' ? { shrink: true } : undefined}
      inputProps={{
        min: field.min ?? undefined,
        max: field.max ?? undefined,
        step: field.type === 'float' ? 'any' : undefined,
      }}
      value={value ?? ''}
      onChange={e => {
        const raw = e.target.value;
        setFieldValue(name, isNumeric ? (raw === '' ? '' : Number(raw)) : raw);
      }}
    />
  );
}

SchemaField.propTypes = {
  sectionKey: PropTypes.string.isRequired,
  field: PropTypes.object.isRequired,
};

export default function SchemaSection({ section }) {
  const { t } = useTranslation();
  const fields = section.fields || [];
  // An all-boolean section (e.g. the 2PDC per-hour status-request flags) is a
  // bank of short switches, not a form of full-width fields — lay it out as a
  // compact grid instead of one switch per row.
  const isAllBoolSection = fields.length > 0 && fields.every(f => f.type === 'bool');
  const fieldLayoutSx =
    section.key === 'thresholds'
      ? {
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 240px))' },
          gap: 2,
          maxWidth: 520,
        }
      : isAllBoolSection
        ? {
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(3, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(6, minmax(0, 1fr))',
            },
            columnGap: 1,
            rowGap: 0.5,
            maxWidth: 720,
          }
        : {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxWidth: 520,
          };

  return (
    <Box component="section" sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t(section.label_key, section.key)}
      </Typography>
      <Box sx={fieldLayoutSx}>
        {fields.map(field => (
          <SchemaField key={field.name} sectionKey={section.key} field={field} />
        ))}
      </Box>
    </Box>
  );
}

SchemaSection.propTypes = {
  section: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label_key: PropTypes.string,
    fields: PropTypes.array,
  }).isRequired,
};
